import React from 'react';
import useSeo from '../lib/useSeo';
import useJsonLd from '../lib/useJsonLd';
import { Link } from 'react-router-dom';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import Reveal from '../components/site/Reveal';
import CountUp from '../components/site/CountUp';
import EnrichIQMockup from '../components/site/EnrichIQMockup';
import { track, events } from '../lib/analytics';
import {
  Target,
  Brain,
  Zap,
  ShieldCheck,
  Radar,
  Radio,
  Contact,
  Users2,
  Play,
  Sparkles,
  ChevronRight,
  ArrowRight,
  Check,
} from 'lucide-react';

const features = [
  {
    icon: Target,
    tag: 'Core differentiator',
    title: 'Two scores. Not one.',
    body:
      "Generic intent tools tell you a company is active. They don't tell you if they're right for your client. Enrich IQ scores both — opportunity (live signals: hiring, funding, news, tech stack) and personalised fit against your client's specific offering, with transparent rationale.",
  },
  {
    icon: Brain,
    tag: 'Per lead',
    title: 'Complete intelligence dossier',
    body:
      'Tech stack signals, real-time intent, verified contacts, buying committee mapping, engagement playbook with timing windows and personalised first-line openers. Not a row in a spreadsheet — a sales-ready package.',
  },
  {
    icon: Zap,
    tag: 'Built in',
    title: 'AI outreach',
    body:
      'Enrich IQ drafts personalised emails from enrichment data, sends from your inbox, tracks opens and replies, and auto-pauses sequences on engagement. Data to outbox in three clicks.',
  },
  {
    icon: ShieldCheck,
    tag: 'Verification',
    title: 'Multi-layer accuracy',
    body:
      "Directory match, deliverability check, smart fallback. Every lead verified before a client ever sees it. Your partner's brand goes on every deliverable — it has to be right.",
  },
];

const layers = [
  { icon: Radar, label: 'Signal', body: 'Company-level intent — hiring, funding, news, tech-stack shifts.' },
  { icon: Radio, label: 'Intent', body: 'Behavioural buying signals scored against your category.' },
  { icon: Contact, label: 'Contact', body: 'Verified emails and phones across three independent layers.' },
  { icon: Users2, label: 'Committee', body: 'Buying-group map — decision-maker, influencer, blocker.' },
  { icon: Play, label: 'Play', body: 'Engagement playbook with timing windows and personalised openers.' },
  { icon: Sparkles, label: 'Fit', body: "Personalised score against your client's specific offering." },
];

const stats = [
  { v: '6', k: 'Signal layers per lead' },
  { v: '3', k: 'Verification layers' },
  { v: '165M+', k: 'Professionals reachable' },
  { v: '95%+', k: 'Contact accuracy' },
];

const guarantees = [
  'Verified before your client sees it',
  'Failing leads auto-replaced at no cost',
  'Your partner brand on every deliverable',
];

const EnrichIQPage = () => {
  useSeo({
    title: 'EnrichIQ — Verified data enrichment for B2B publishers',
    description: 'EnrichIQ enriches every lead your audience generates with firmographic and intent data, so your buyers get a verified, sales-ready record instead of a raw form submission.',
    path: '/products/enrich-iq',
  });
  useJsonLd([
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': 'https://fivedbs.com/products/enrich-iq#service',
      name: 'EnrichIQ',
      alternateName: 'Enrich IQ',
      serviceType: 'B2B lead data enrichment',
      description: 'EnrichIQ enriches every lead a publisher audience generates with firmographic, technographic and intent data. Each record is verified before it is handed to the buying sales team, replacing raw form submissions with a sales-ready contact.',
      provider: { '@id': 'https://fivedbs.com/#organization' },
      areaServed: [
        { '@type': 'Place', name: 'Global' },
        { '@type': 'Place', name: 'North America' },
        { '@type': 'Place', name: 'United Kingdom' },
        { '@type': 'Place', name: 'European Union' },
        { '@type': 'Place', name: 'APAC' },
      ],
      audience: {
        '@type': 'Audience',
        audienceType: 'B2B publishers, media brands and demand-generation teams',
      },
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        url: 'https://fivedbs.com/contact',
        priceCurrency: 'USD',
      },
      url: 'https://fivedbs.com/products/enrich-iq',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://fivedbs.com/' },
        { '@type': 'ListItem', position: 2, name: 'Products', item: 'https://fivedbs.com/#products' },
        { '@type': 'ListItem', position: 3, name: 'EnrichIQ', item: 'https://fivedbs.com/products/enrich-iq' },
      ],
    },
  ]);
  return (
    <div className="min-h-screen bg-white font-sans text-navy">
      <Navbar />
      <main>
        {/* Hero */}
        <section
          className="relative min-h-[70vh] flex items-center overflow-hidden pt-28"
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(26,115,232,0.30) 0%, rgba(20,30,52,0.90) 60%, rgba(20,30,52,0.95) 100%), url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&q=80')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="relative max-w-6xl mx-auto w-full px-6 lg:px-8 py-20">
            <nav className="flex items-center gap-2 text-slate-300 text-[12px] font-bold uppercase tracking-[0.14em] mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <ChevronRight size={14} />
              <Link to="/#products" className="hover:text-white">Products</Link>
              <ChevronRight size={14} />
              <span className="text-white">Enrich IQ</span>
            </nav>
            <p className="text-brand-blue-light text-[12px] font-bold tracking-[0.16em] uppercase mb-5">
              Product · Enrich IQ
            </p>
            <h1 className="text-white font-black tracking-[-0.01em] leading-[1.05] text-[44px] sm:text-[58px] md:text-[68px] max-w-[900px]">
              Every lead, a complete dossier.
            </h1>
            <p className="mt-7 text-slate-200/90 text-[17px] leading-[1.7] max-w-[720px]">
              Not a contact card. A complete intelligence dossier per lead — the kind your SDR would build manually in 15 minutes, assembled in seconds by AI that actually researches, not retrieves.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="https://leadiqpro.io/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center h-11 px-7 rounded-md bg-brand-blue hover:bg-brand-blue-dark text-white text-[13px] font-bold uppercase tracking-[0.08em] shadow-mk-blue hover:shadow-mk-blue-hover hover:-translate-y-0.5 transition-all duration-300"
              >
                Try LeadIQ Pro
                <ArrowRight size={16} className="ml-2" />
              </a>
              <Link
                to="/#products"
                className="inline-flex items-center justify-center h-11 px-7 rounded-md bg-white/[0.06] backdrop-blur border border-white/25 text-white text-[13px] font-bold uppercase tracking-[0.08em] hover:bg-white/[0.12] transition-all duration-300"
              >
                Explore all products
              </Link>
            </div>
          </div>
        </section>

        {/* Machine-readable answer block — surfaces direct answers to "What is EnrichIQ?"
            style queries in AI answer engines (ChatGPT, Perplexity, Google AI Overviews). */}
        <section className="bg-white py-14 border-b border-slate-100">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-8">
            <div>
              <h2 className="text-navy font-black text-[22px] md:text-[26px] leading-[1.25]">What is EnrichIQ?</h2>
              <p className="mt-3 text-body-muted text-[15px] md:text-[16px] leading-[1.75]">EnrichIQ is a B2B lead-enrichment service from 5DBS that appends verified firmographic, technographic and intent data to every lead a publisher audience generates. Instead of a raw form submission, buyers receive a sales-ready contact record with company, role, seniority, and buying-signal context — the same dossier an SDR would build manually, assembled in seconds.</p>
            </div>
            <div>
              <h2 className="text-navy font-black text-[22px] md:text-[26px] leading-[1.25]">Who is EnrichIQ for?</h2>
              <p className="mt-3 text-body-muted text-[15px] md:text-[16px] leading-[1.75]">EnrichIQ is built for B2B publishers, media brands and demand-generation teams that need to hand off leads their buyers will actually work. It fits any team running gated content, lead-gen campaigns or webinars where the raw form data alone is not enough to justify a CPL.</p>
            </div>
            <div>
              <h2 className="text-navy font-black text-[22px] md:text-[26px] leading-[1.25]">How does EnrichIQ work?</h2>
              <p className="mt-3 text-body-muted text-[15px] md:text-[16px] leading-[1.75]">Each new lead is passed through 5DBS's enrichment pipeline: identity resolution against a first-party graph, company and technographic append, intent signal matching, and human-in-the-loop verification. The final record is delivered to the buyer's CRM or lead-gen platform in real time, with an audit trail showing how every field was verified.</p>
            </div>
          </div>
        </section>

        {/* Stats band */}
        <section className="bg-brand-soft py-16">
          <div className="max-w-6xl mx-auto px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <Reveal key={s.k} delay={i * 90} className="text-center">
                <div className="text-brand-blue text-[36px] sm:text-[42px] font-black leading-none tracking-tight">
                  <CountUp value={s.v} />
                </div>
                <p className="mt-3 text-body-muted text-[13.5px] font-semibold">{s.k}</p>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Features */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-brand-blue text-[12px] font-bold tracking-[0.16em] uppercase mb-4">
                What makes it different
              </p>
              <h2 className="text-navy font-black tracking-[-0.01em] leading-[1.12] text-[32px] sm:text-[40px] md:text-[46px]">
                Four things generic intent tools can't do
              </h2>
            </div>

            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.map((f, i) => {
                const Icon = f.icon;
                return (
                  <Reveal
                    key={f.title}
                    delay={i * 110}
                    className="bg-white rounded-2xl shadow-mk p-7 flex gap-5 hover:shadow-mk-lg hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="shrink-0 inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-brand-blue to-brand-blue-light text-white shadow-mk-blue">
                      <Icon size={22} strokeWidth={2.2} />
                    </div>
                    <div>
                      <span className="inline-block text-brand-blue text-[10.5px] font-bold uppercase tracking-[0.14em] bg-brand-blue/10 rounded-full px-2.5 py-0.5">
                        {f.tag}
                      </span>
                      <h3 className="mt-3 text-navy text-[19px] font-bold">{f.title}</h3>
                      <p className="mt-2 text-body-muted text-[14.5px] leading-[1.7]">{f.body}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Six Layers */}
        <section className="bg-brand-soft py-20 md:py-24">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-brand-blue text-[12px] font-bold tracking-[0.16em] uppercase mb-4">
                Six layers per LeadCard
              </p>
              <h2 className="text-navy font-black tracking-[-0.01em] leading-[1.12] text-[32px] sm:text-[40px] md:text-[46px]">
                Signal → Intent → Contact → Committee → Play → Fit
              </h2>
            </div>
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {layers.map((l, i) => {
                const Icon = l.icon;
                return (
                  <Reveal
                    key={l.label}
                    delay={i * 100}
                    className="relative bg-white rounded-2xl shadow-mk p-6 pt-14 hover:shadow-mk-lg hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="absolute -top-6 left-6 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-brand-blue to-brand-blue-light text-white shadow-mk-blue">
                      <Icon size={20} strokeWidth={2.2} />
                    </div>
                    <span className="text-brand-blue/15 text-[36px] font-black leading-none absolute top-4 right-5">
                      0{i + 1}
                    </span>
                    <p className="text-brand-blue text-[11px] font-bold uppercase tracking-[0.14em]">
                      Layer {i + 1}
                    </p>
                    <h3 className="mt-1 text-navy text-[20px] font-bold">{l.label}</h3>
                    <p className="mt-2 text-body-muted text-[14px] leading-[1.7]">{l.body}</p>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Live mockup */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-brand-blue text-[12px] font-bold tracking-[0.16em] uppercase mb-4">
                See it in the wild
              </p>
              <h2 className="text-navy font-black tracking-[-0.01em] leading-[1.12] text-[32px] sm:text-[40px] md:text-[46px]">
                A LeadCard your team can send to a client
              </h2>
              <p className="mt-6 text-body-muted text-[16px] leading-[1.75]">
                Dual scores, tech-stack signals, buying committee mapped, verified contacts — all in the same card, ready to hand off.
              </p>
            </div>

            <Reveal className="mt-12 max-w-5xl mx-auto">
              <EnrichIQMockup />
              <p className="mt-4 text-center text-body-muted text-[13px] italic">
                A live view of what your publisher partners see under their brand.
              </p>
            </Reveal>
          </div>
        </section>

        {/* Dual score */}
        <section className="bg-brand-soft py-20 md:py-24">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto">
              <p className="text-brand-blue text-[12px] font-bold tracking-[0.16em] uppercase mb-4">
                The dual score
              </p>
              <h2 className="text-navy font-black tracking-[-0.01em] leading-[1.12] text-[30px] sm:text-[36px] md:text-[42px]">
                Two scores per lead. Independent axes.
              </h2>
              <p className="mt-6 text-body-muted text-[16px] leading-[1.75]">
                Opportunity tells you they're active. Fit tells you they're right. Never conflated — because a hot company that isn't a fit is a wasted call.
              </p>
            </div>

            <Reveal className="mt-12">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="rounded-2xl bg-white shadow-mk p-7 border-l-[4px] border-l-[#D4922A]">
                  <p className="text-[#D4922A] text-[11px] font-bold uppercase tracking-[0.14em]">
                    Opportunity
                  </p>
                  <div className="mt-2 text-navy text-[54px] font-black leading-none tracking-tight">85</div>
                  <p className="mt-3 text-body-muted text-[14px] leading-[1.6]">
                    Hiring 3 engineers, Series B closed, expanding into EMEA
                  </p>
                </div>
                <div className="rounded-2xl bg-white shadow-mk p-7 border-l-[4px] border-l-[#22C55E]">
                  <p className="text-[#22C55E] text-[11px] font-bold uppercase tracking-[0.14em]">
                    Fit
                  </p>
                  <div className="mt-2 text-navy text-[54px] font-black leading-none tracking-tight">25</div>
                  <p className="mt-3 text-body-muted text-[14px] leading-[1.6]">
                    Uses competitor stack, no budget signal for your category
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Guarantees */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <p className="text-brand-blue text-[12px] font-bold tracking-[0.16em] uppercase mb-4">
              What your partners can promise
            </p>
            <h2 className="text-navy font-black tracking-[-0.01em] leading-[1.12] text-[32px] sm:text-[40px] md:text-[46px]">
              Every lead verified. Every deliverable branded.
            </h2>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {guarantees.map((c) => (
                <div
                  key={c}
                  className="flex items-center gap-3 rounded-2xl bg-brand-soft border border-slate-100 px-5 py-4"
                >
                  <span className="h-9 w-9 rounded-xl bg-gradient-to-br from-brand-blue to-brand-blue-light text-white flex items-center justify-center shrink-0 shadow-mk-blue">
                    <Check size={16} strokeWidth={3} />
                  </span>
                  <span className="text-navy text-[13.5px] font-bold text-left">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section
          className="relative py-24"
          style={{ background: 'linear-gradient(135deg, #1A2035 0%, #344767 100%)' }}
        >
          <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-white font-black tracking-[-0.01em] leading-[1.15] text-[30px] sm:text-[40px]">
              See Enrich IQ inside 15 minutes
            </h2>
            <p className="mt-5 text-slate-300 text-[16px] leading-[1.7]">
              Bring one advertiser and one industry. We'll show you the LeadCard we'd produce, on your specs, live on the call.
            </p>
            <div className="mt-9 flex flex-wrap gap-3 justify-center">
              <a
                href="https://leadiqpro.io/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center h-12 px-8 rounded-md bg-brand-blue hover:bg-brand-blue-dark text-white text-[13px] font-bold uppercase tracking-[0.08em] shadow-mk-blue hover:shadow-mk-blue-hover hover:-translate-y-0.5 transition-all duration-300"
              >
                Try LeadIQ Pro
              </a>
              <Link
                to="/contact"
                data-testid="enrich-iq-grab-15-btn"
                onClick={() => track(events.GRAB_15_CALL_CLICK, { location: 'enrich_iq' })}
                className="inline-flex items-center justify-center h-12 px-8 rounded-md bg-white/[0.06] backdrop-blur border border-white/25 text-white text-[13px] font-bold uppercase tracking-[0.08em] hover:bg-white/[0.12] transition-all duration-300"
              >
                Grab a 15-minute call
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default EnrichIQPage;
